import { unlimitedApprove, mint, getAllowance, checkIsAddedToTimelock, send, getIsShouldEnterTimelock, executeLzReceive, sendWithPermit } from '../../api'
import BigNumber from 'bignumber.js'
import { v4 as uuidv4 } from 'uuid'
import * as Sumer from '@meterio/sumer-js'
import { liquidateBorrow } from '../../api/shylock'
import router from '../../router'
import { UnknowError } from '../../constants'
import { ethers, zeroPadValue } from 'ethers'
import { LayerZeroV2TimelockABI } from '../../abi'
import { Options } from '@layerzerolabs/lz-v2-utilities'

import { createWalletClient, createPublicClient, custom, parseEther, formatEther, formatUnits, http } from 'viem'
import { getContracts, getQuote, generateOrder, withExecAndTransfer, validateOrder, sendOrder, waitForOrderOpen, waitForOrderClose } from '@omni-network/core'
import { arbitrumSepolia, baseSepolia } from 'viem/chains'

const namespaced = true

const state = {
  /**
   * {
   *  id: string,
   *  title: string,
   *  hash: string,
   *  symbol: string,
   *  status: string, // ok,fail,pending, confirm
   *  show: boolean,
   *  msg: string,
   *  amount: string,
   * }
   */
  txInfos: [],
}

const getters = {}

const mutations = {
  setTxInfos(state, { id, title, hash, symbol, status, show, msg, amount, timelocked }) {
    const txInfoIndex = state.txInfos.findIndex((t) => t.id === id)
    if (txInfoIndex !== -1) {
      const aimData = { ...state.txInfos[txInfoIndex] }
      let _title = title
      let _hash = hash
      let _symbol = symbol
      let _status = status
      let _show = show
      let _amount = amount
      let _timelocked = timelocked
      if (!_title) {
        _title = aimData.title
      }
      if (!_hash) {
        _hash = aimData.hash
      }
      if (!_symbol) {
        _symbol = aimData.symbol
      }
      if (!_status) {
        _status = aimData.status
      }
      if (_show === undefined) {
        _show = aimData.show
      }
      if (_amount === undefined) {
        _amount = aimData.amount
      }
      if (_timelocked === undefined) {
        _timelocked = aimData.timelocked
      }
      state.txInfos.splice(txInfoIndex, 1, {
        id,
        title: _title,
        hash: _hash,
        symbol: _symbol,
        status: _status,
        show: _show,
        msg,
        amount: _amount,
        timelocked: _timelocked
      })
    } else {
      state.txInfos.unshift({ id, title, hash, symbol, status, msg, show: true, amount, timelocked })
    }

    localStorage.removeItem('sumer-ui-txs')
    localStorage.setItem('sumer-ui-txs', JSON.stringify(state.txInfos))
  },
  clearStoredTx(state) {
    localStorage.removeItem('sumer-ui-txs')
    state.txInfos = []
  },
  clearTxInfos(state) {
    state.txInfos = state.txInfos.map((t) => ({
      ...t,
      show: false,
    }))
  },
}

const actions = {
  async approve({ rootState, commit, dispatch }, { symbol, token, spender, flag }) {
    const id = uuidv4()
    commit('setTxInfos', {
      id,
      title: 'Enable',
      symbol: symbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
    })
    try {
      const tx = await unlimitedApprove(rootState.wallet.sumer, token, spender)
      console.log(`sent tx:`, tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log(`approve receipt:`, receipt)
      try {
        await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })
      if (flag) {
        await dispatch('market/initMarket', null, { root: true })
      }

      return true
    } catch (e) {
      console.log('approve error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async supply({ rootState, commit, dispatch }, { underlySymbol, amount }) {
    console.log('supply underlySymbol', underlySymbol)
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()
    try {
      if (amount instanceof BigNumber) {
        amount = amount.toFixed(0)
      }

      const { sumer, account } = rootState.wallet

      const pair = sumer.getTokenPairBySymbol(underlySymbol)

      let needApprove = false

      const underlyAddress = Sumer.util.getAddress(underlySymbol, sumer._network.name)
      if (ethers.ZeroAddress !== underlyAddress) {
        const decimals = Sumer.util.getUnderlyDecimals(underlySymbol, sumer._network.name)
        const sdrTokenAddress = Sumer.util.getAddress(`sdr${underlySymbol}`, sumer._network.name)
        const allowance = await getAllowance(sumer, underlyAddress, account, sdrTokenAddress)
        if (new BigNumber(String(allowance)).div(`1e${decimals}`).lt(amount)) {
          needApprove = true
          if (!(pair && pair.permitEnabled)) {
            const res = await dispatch('approve', {
              symbol: underlySymbol,
              token: underlyAddress,
              spender: sdrTokenAddress,
            })
            if (!res) {
              return
            }
          }
        }
      }

      commit('setTxInfos', {
        id,
        title: 'Deposit',
        symbol: underlySymbol,
        status: 'confirm',
        msg: 'Confirm the transaction.',
        amount: new BigNumber(amount).toFixed(),
      })

      // const trxOptions = { mantissa: false }

      let tx
      if (pair && pair.permitEnabled && needApprove) {
        tx = await sumer.supplyWithPermit(underlySymbol, amount)
      } else {
        tx = await sumer.supply(underlySymbol, amount, true) //, trxOptions)
      }
      commit('modal/HIDE_DEPOSIT', null, { root: true })
      console.log('sent tx: ', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('supply receipt: ', receipt)

      try {
        await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        await tx.wait()
      }

      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('supply error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async redeem({ rootState, commit, dispatch }, { underlySymbol, underlyOrCTokenSymbol, amount, noDecimalsAmount }) {

    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }

    const { sumer } = rootState.wallet

    const timelocked = await checkIsAddedToTimelock(sumer, underlySymbol, noDecimalsAmount)

    const id = uuidv4()
    commit('setTxInfos', {
      id,
      title: 'Withdraw',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
      amount: noDecimalsAmount,
      timelocked
    })
    try {
      if (amount instanceof BigNumber) {
        amount = amount.toFixed(0)
      }
      const tx = await sumer.redeem(underlyOrCTokenSymbol, amount, {
        mantissa: true,
      })
      commit('modal/HIDE_DEPOSIT', null, { root: true })
      console.log('sent tx: ', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })

      let receipt = false;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      let msg = 'Transaction success.'

      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('redeem error: ', e)
      let msg = getErrorMsg(e)
      if (msg === 'evm: execution reverted: insufficient liquidity') {
        msg = 'You cannot withdraw this asset. This asset collateralizes an open borrow position'
      }
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async borrowToken({ rootState, commit, dispatch }, { underlySymbol, amount }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }

    const { sumer } = rootState.wallet

    const timelocked = await checkIsAddedToTimelock(sumer, underlySymbol, amount)

    const id = uuidv4()
    commit('setTxInfos', {
      id,
      title: 'Borrow',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
      amount: new BigNumber(amount).toFixed(),
      timelocked
    })
    try {
      if (amount instanceof BigNumber) {
        amount = amount.toFixed(0)
      }

      const trxOptions = { mantissa: false }
      const tx = await sumer.borrow(underlySymbol, amount, trxOptions)
      commit('modal/HIDE_BORROW', null, { root: true })
      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })

      let receipt = false;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', {
        id,
        hash: tx.hash,
        status: 'ok',
        msg: 'Transaction success.',
      })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('borrow error: ', { ...e })
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async repayBorrow({ rootState, commit, dispatch }, { underlySymbol, amount, noDecimalsAmount, isCEther, isMax }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()
    try {
      if (amount instanceof BigNumber) {
        amount = amount.toFixed(0)
      }

      const { sumer, account } = rootState.wallet
      const pair = sumer.getTokenPairBySymbol(underlySymbol)

      const underlyAddress = Sumer.util.getAddress(underlySymbol, sumer._network.name)

      let needApprove = false

      if (underlyAddress !== ethers.ZeroAddress) {
        const sdrTokenAddress = Sumer.util.getAddress(`sdr${underlySymbol}`, sumer._network.name)
        const allowance = await getAllowance(sumer, underlyAddress, account, sdrTokenAddress)
        if (new BigNumber(String(allowance)).lt(amount)) {
          needApprove = true
          if (!(pair && pair.permitEnabled)) {
            const res = await dispatch('approve', {
              symbol: underlySymbol,
              token: underlyAddress,
              spender: sdrTokenAddress,
            })
            if (!res) {
              return
            }
          }
        }
      }

      commit('setTxInfos', {
        id,
        title: 'RepayBorrow',
        symbol: underlySymbol,
        status: 'confirm',
        msg: 'Confirm the transaction.',
        amount: noDecimalsAmount,
      })

      let tx
      if (pair && pair.permitEnabled && needApprove) {
        tx = await sumer.repayBorrowWithPermit(underlySymbol, amount, {
          mantissa: true,
        })
      } else {
        tx = await sumer.repayBorrow(underlySymbol, amount, '', true, {
          mantissa: true,
        })
      }
      commit('modal/HIDE_BORROW', null, { root: true })
      console.log(`sent tx:`, tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('repay receipt: ', receipt)
      let receipt;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('repayBorrow error:', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async enterMarkets({ rootState, commit, dispatch }, { underlySymbol, sdrSymbol }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()
    commit('setTxInfos', {
      id,
      title: 'Enter Markets',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
    })
    try {
      const tx = await rootState.wallet.sumer.enterMarkets(sdrSymbol)
      commit('modal/HIDE_COLLATERAL', null, { root: true })
      console.log(`sent tx:`, tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('enter markets receipt: ', receipt)
      let receipt;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('enter markets error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async exitMarkets({ rootState, commit, dispatch }, { sdrSymbol, underlySymbol }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()
    commit('setTxInfos', {
      id,
      title: 'Exit Markets',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
    })
    try {
      const tx = await rootState.wallet.sumer.exitMarket(sdrSymbol)
      commit('modal/HIDE_COLLATERAL', null, { root: true })
      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('exit markets receipt: ', receipt)

      let receipt;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('exit markets error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async mintAction({ rootState, commit, dispatch }, { underlySymbol, sdrTokenAddress, amount, decimals }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()

    const { sumer } = rootState.wallet

    const timelocked = await checkIsAddedToTimelock(sumer, underlySymbol, amount)
    commit('setTxInfos', {
      id,
      title: 'Mint',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
      amount: new BigNumber(amount).toFixed(),
      timelocked
    })
    try {
      const tx = await mint(sumer, sdrTokenAddress, amount, decimals)
      commit('modal/HIDE_MINT_SYN', null, { root: true })
      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      let receipt = false;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', {
        id,
        hash: tx.hash,
        status: 'ok',
        msg: 'Transaction success.',
      })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('mint error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },

  async redeemFaceValueAction(
    { rootState, commit, dispatch },
    { underlySymbol, amount, decimals, providers, deadline, signature },
  ) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { sumer, account } = rootState.wallet

    const timelocked = await checkIsAddedToTimelock(sumer, underlySymbol, amount)

    const id = uuidv4()


    try {

      const pair = sumer.getTokenPairBySymbol(underlySymbol)

      let needApprove = false

      const tokenAddress = Sumer.getAddress(sumer._network.name, underlySymbol)
      const sdrTokenAddress = Sumer.getAddress(sumer._network.name, `sdr${underlySymbol}`)
      const allowance = await getAllowance(sumer, tokenAddress, account, sdrTokenAddress)
      if (new BigNumber(String(allowance)).div(`1e${decimals}`).lt(amount)) {
        needApprove = true
        if (!(pair && pair.permitEnabled)) {
          const res = await dispatch('approve', {
            symbol: underlySymbol,
            token: tokenAddress,
            spender: sdrTokenAddress,
          })
          if (!res) {
            return
          }
        }
        // const res = await dispatch('approve', { symbol: underlySymbol, token: tokenAddress, spender: sdrTokenAddress })
        // if (!res) {
        //   return
        // }
      }

      console.log('entered redeem face value')
      console.log(`underly: ${underlySymbol}, amount: ${amount}`)
      commit('setTxInfos', {
        id,
        title: 'RedeemFaceValue',
        symbol: underlySymbol,
        status: 'confirm',
        msg: 'Confirm the transaction.',
        amount: new BigNumber(amount).toFixed(),
        timelocked
      })

      let tx
      if (pair && pair.permitEnabled && needApprove) {
        tx = await sumer.redeemFaceValueWithPermit(
          underlySymbol,
          BigNumber(amount).times(`1e${decimals}`).toFixed(0),
          providers,
          deadline,
          signature,
        )
      } else {
        // tx = await sumer.redeemFaceValue(underlySymbol, BigNumber(amount).times(`1e${decimals}`).toFixed(0))
        tx = await sumer.redeemFaceValue(
          underlySymbol,
          BigNumber(amount).times(`1e${decimals}`).toFixed(0),
          providers,
          deadline,
          signature,
        )
      }
      commit('modal/HIDE_REDEMPTION', null, { root: true })
      console.log('redeem face value', underlySymbol, amount, providers, deadline, signature)

      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('redeemFaceValue receipt: ', receipt)

      let receipt = false;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', {
        id,
        hash: tx.hash,
        status: 'ok',
        msg: 'Transaction success.',
      })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('redemption error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },

  async protectedMintSynAction(
    { rootState, commit, dispatch },
    { underlySymbol, collateralSymbol, borrowAmount, mintAmount },
  ) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()
    console.log('entered protected mint syn')
    console.log(
      `underlySymbol:${underlySymbol}, collateral: ${collateralSymbol}, borrowAmount: ${borrowAmount}, mintAmount: ${mintAmount}`,
    )
    commit('setTxInfos', {
      id,
      title: 'ProtectedMint',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
      amount: mintAmount,
    })
    try {
      const tx = await rootState.wallet.sumer.protectedMint(underlySymbol, collateralSymbol, borrowAmount, mintAmount)
      commit('modal/HIDE_PROTECTED_MINT_SYN', null, { root: true })
      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('protectedMint receipt: ', receipt)

      let receipt, agreementCreatedEvent = false;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', {
        id,
        hash: tx.hash,
        status: 'ok',
        msg: 'Transaction success.',
      })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('protectedMint error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },

  async paybackAction({ rootState, commit, dispatch }, { underlySymbol, amount, noDecimalsAmount }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const id = uuidv4()
    try {
      if (amount instanceof BigNumber) {
        amount = amount.toFixed(0)
      }

      const { sumer, account } = rootState.wallet

      let needApprove = false

      const pair = sumer.getTokenPairBySymbol(underlySymbol)
      const underlyAddress = Sumer.util.getAddress(underlySymbol, sumer._network.name)
      const sdrTokenAddress = Sumer.util.getAddress(`sdr${underlySymbol}`, sumer._network.name)
      const allowance = await getAllowance(sumer, underlyAddress, account, sdrTokenAddress)

      if (new BigNumber(String(allowance)).lt(amount)) {
        needApprove = true
        if (!(pair && pair.permitEnabled)) {
          const res = await dispatch('approve', {
            symbol: underlySymbol,
            token: underlyAddress,
            spender: sdrTokenAddress,
          })
          if (!res) return
        }
      }
      commit('setTxInfos', {
        id,
        title: 'Payback',
        symbol: underlySymbol,
        status: 'confirm',
        msg: 'Confirm the transaction.',
        amount: noDecimalsAmount,
      })

      let tx
      if (pair && pair.permitEnabled && needApprove) {
        tx = await sumer.repayBorrowWithPermit(underlySymbol, amount, {
          mantissa: true,
        })
      } else {
        tx = await sumer.repayBorrow(underlySymbol, amount, '', true, {
          mantissa: true,
        })
      }
      commit('modal/HIDE_MINT_SYN', null, { root: true })
      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('payback receipt: ', receipt)

      let receipt;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      await dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('payback error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async liquidateBorrow(
    { rootState, commit, dispatch },
    { borrower, underlySymbol, sdrTokenAddress, amount, cTokenCollateral },
  ) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { account, sumer } = rootState.wallet
    const decimals = Sumer.getDecimals(sumer._network.name, underlySymbol)
    const underlyAddress = Sumer.getAddress(sumer._network.name, underlySymbol)
    if (underlyAddress !== ethers.ZeroAddress) {
      const allowance = await getAllowance(sumer, underlyAddress, account, sdrTokenAddress)
      if (new BigNumber(String(allowance)).isLessThan(amount)) {
        const res = await dispatch('approve', {
          symbol: underlySymbol,
          token: underlyAddress,
          spender: sdrTokenAddress,
        })
        if (!res) return
      }
    }

    const id = uuidv4()
    commit('setTxInfos', {
      id,
      title: 'Liquidate',
      symbol: underlySymbol,
      status: 'confirm',
      msg: 'Confirm the transaction.',
      amount: new BigNumber(amount).div(`1e${decimals}`),
    })
    try {
      if (amount instanceof BigNumber) {
        amount = amount.toFixed(0)
      }

      console.log(
        `calling liquidate borrow borrower:${borrower}, sdrToken:${sdrTokenAddress}, amount:${amount}, cTokenCollateral:${cTokenCollateral}`,
      )
      const tx = await liquidateBorrow(sumer, borrower, sdrTokenAddress, amount, cTokenCollateral)
      console.log('sent tx:', tx)
      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      // const receipt = await tx.wait()
      // console.log('LiquidateBorrow receipt: ', receipt)
      let receipt;
      try {
        receipt = await tx.wait()
      } catch (e) {
        console.log('tx wait error', e)
        receipt = await tx.wait()
      }

      commit('setTxInfos', {
        id,
        hash: tx.hash,
        status: 'ok',
        msg: 'Transaction success.',
      })
      const planAccount = router.currentRoute.params.account
      await dispatch('shylock/getPlans', { account: planAccount }, { root: true })
    } catch (e) {
      console.log('LiquidateBorrow error: ', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, show: true, status: 'fail', msg })
    }
  },
  async send({ rootState, commit, dispatch }, { symbol, tokenAddress, fee, toAddress, currentToChain, amount }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { sumer, currentNetwork, account } = rootState.wallet
    const { proxyAddr } = currentNetwork.oftBridge2
    const { eid, GAS_LIMIT, MSG_VALUE } = currentToChain.oftBridge2
    const id = uuidv4()

    try {
      // const allowance = await getAllowance(sumer, tokenAddress, account, proxyAddr)
      // if (new BigNumber(String(allowance)).lt(amount)) {
      //   const res = await dispatch('approve', {
      //     symbol: symbol,
      //     token: tokenAddress,
      //     spender: proxyAddr,
      //   })
      //   if (!res) {
      //     return
      //   }
      // }

      const params = [
        tokenAddress,
        amount,
      ]

      const shouldEnterTimelock = await getIsShouldEnterTimelock(sumer, {
        oftAddress: proxyAddr,
        params
      })
      console.log('shouldEnterTimelock', shouldEnterTimelock)

      const _options = Options.newOptions().addExecutorLzReceiveOption(
        shouldEnterTimelock ? GAS_LIMIT.Max : GAS_LIMIT.Min,
        MSG_VALUE
      );

      const sendParam = {
        dstEid: eid,
        to: zeroPadValue(toAddress, 32),
        token: tokenAddress,
        amountLD: amount,
        minAmountLD: amount,
        extraOptions: _options.toHex(),
        composeMsg: "0x",
        oftCmd: "0x",
      };

      commit('setTxInfos', {
        id,
        title: 'transfer',
        symbol,
        status: 'confirm',
        msg: 'Confirm the transaction.',
        amount: new BigNumber(amount).div(1e18),
        timelocked: shouldEnterTimelock
      })

      const tx = await sendWithPermit(sumer, {
        oftAddress: proxyAddr,
        sendParam,
        toAddress,
        shouldEnterTimelock,
        fee,
      })

      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      try {
        await tx.wait()
      } catch (e) {
        await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })
    } catch (e) {
      console.log('bridge error', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async executeLzReceive({ rootState, commit, dispatch }, data) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { sumer, currentNetwork, account } = rootState.wallet
    const { epointAddr } = currentNetwork.oftBridge2
    const { origin, guid, receiver, message, extraData, value } = data
    const id = uuidv4()

    try {

      commit('setTxInfos', {
        id,
        title: 'execute',
        symbol: '',
        status: 'confirm',
        msg: 'Confirm the transaction.',
        amount: new BigNumber(value).toFixed(),
      })
      const { srcEid, sender, nonce } = origin.toObject()
      const params = [[srcEid, sender, nonce], receiver, guid, message, extraData]

      const tx = await executeLzReceive(sumer, {
        LzEndpointAddr: epointAddr,
        params
      })

      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      try {
        await tx.wait()
      } catch (e) {
        await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })
    } catch (e) {
      console.log('exe lzReceive error', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async timelockClaim({ rootState, commit, dispatch }, { agreementIds, amount, symbol }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { sumer, signer } = rootState.wallet
    const timelockAddr = Sumer.getAddress(sumer._network.name, 'Timelock')

    const timelockContract = new Sumer._ethers.Contract(timelockAddr, Sumer.abi.Timelock, signer)

    const id = uuidv4()
    commit('setTxInfos', { id, title: 'Claim', symbol, status: 'confirm', msg: 'Confirm the transaction.', amount })

    try {
      console.log('agreementIds', agreementIds)
      const tx = await timelockContract.claim(agreementIds)

      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      try {
        await tx.wait()
      } catch (e) {
        await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      // dispatch('market/getUserAgreements', null, { root: true })
      dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('timelockClaim error', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async bridgeTimelockClaim({ rootState, commit, dispatch }, { idxs, amount, symbol }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { sumer, signer, currentNetwork } = rootState.wallet

    const { timelockAddr } = currentNetwork.oftBridge2

    const timelockContract = new Sumer._ethers.Contract(timelockAddr, LayerZeroV2TimelockABI, signer)

    const id = uuidv4()
    commit('setTxInfos', { id, title: 'Claim', symbol, status: 'confirm', msg: 'Confirm the transaction.', amount })

    try {
      const tx = await timelockContract.claim(idxs)

      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      try {
        await tx.wait()
      } catch (e) {
        await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      // dispatch('market/getUserAgreements', null, { root: true })
      dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('timelockClaim error', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async infraredClaim({ rootState, commit, dispatch }, { amount, sdrTokenAddress, symbol }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { account, signer } = rootState.wallet

    const infraredVaultContract = new Sumer._ethers.Contract(sdrTokenAddress, Sumer.abi.CInfraredVault, signer)

    const id = uuidv4()
    commit('setTxInfos', { id, title: 'Claim', symbol, status: 'confirm', msg: 'Confirm the transaction.', amount })

    try {
      const tx = await infraredVaultContract.claimAll()

      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })
      try {
        await tx.wait()
      } catch (e) {
        await tx.wait()
      }
      commit('setTxInfos', { id, hash: tx.hash, status: 'ok', msg: 'Transaction success.' })

      dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('infraredClaim error', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },

  async quoteDepositByOmni({ rootState, commit, dispatch }, { amount, destChain, symbol }) {
    const { sumer, provider, account, currentNetwork } = rootState.wallet
    const fromToken = Sumer.getTokenPairBySymbol(currentNetwork.nameInCompoundjs, symbol)
    const destToken = Sumer.getTokenPairBySymbol(destChain.nameInCompoundjs, symbol)
    const decimalsAmount = new BigNumber(amount).times(`1e${fromToken.underlyDecimal}`).toFixed(0)
    const quoteExpense = await getQuote({
      srcChainId: currentNetwork.networkId,
      destChainId: destChain.networkId,
      deposit: { token: fromToken.underlyAddr, amount: BigInt(decimalsAmount) },
      expense: { token: destToken.underlyAddr },
      mode: 'expense'
    });
    return quoteExpense
  },

  async depositByOmni({ rootState, commit, dispatch }, { amount, destChain, symbol }) {
    const badConnect = await dispatch('badConnect')
    if (badConnect) {
      return alert(badConnect)
    }
    const { sumer, provider, account, currentNetwork } = rootState.wallet
    console.log('destChain', destChain)
    console.log('symbol', symbol)
    const id = uuidv4()

    try {
      // get fromToken
      const fromToken = Sumer.getTokenPairBySymbol(currentNetwork.nameInCompoundjs, symbol)
      const destToken = Sumer.getTokenPairBySymbol(destChain.nameInCompoundjs, symbol)
      console.log('fromToken', fromToken)

      const environment = !!currentNetwork.isTestnet ? 'testnet' : 'mainnet'

      const tokenIsNative = fromToken.ctokenIsEther

      const TargetAddress = destToken.ctokenAddr

      // getContracts
      const contracts = await getContracts({ environment })
      console.log('contracts', contracts)
      const { inbox } = contracts

      // quote
      const quoteExpense = await dispatch('quoteDepositByOmni', { amount, destChain, symbol })
      // console.log('quoteExpense', quoteExpense)
      console.log(`Depositing ${formatUnits(quoteExpense.deposit.amount, fromToken.decimals)} yields ${formatUnits(quoteExpense.expense.amount, destToken.decimals)} on destination`)

      const quotedDepositAmt = quoteExpense.deposit.amount
      const quotedExpenseAmt = quoteExpense.expense.amount

      // check allowance and approve if !native
      if (!tokenIsNative) {
        const allowance = await getAllowance(sumer, fromToken.underlyAddr, account, inbox)
        console.log('allowance:', allowance.toString())

        if (allowance < quotedDepositAmt) {

          const res = await dispatch('approve', {
            symbol: fromToken.underlySym,
            token: fromToken.underlyAddr,
            spender: inbox,
          })
          if (!res) {
            return
          }
        }
      }

      commit('setTxInfos', { id, title: `Deposit to ${destChain.name}`, symbol, status: 'confirm', msg: 'Confirm the transaction.', amount })

      const viemChain = currentNetwork.omni.viemChain
      const walletClient = createWalletClient({
        chain: viemChain,
        transport: custom(provider),
        account
      })

      const targetCall = {
        target: TargetAddress,
        functionName: "mint",
      }
      if (tokenIsNative) {
        targetCall.abi = Sumer.abi.CEther
        // targetCall.args = []
        targetCall.value = quotedExpenseAmt
      } else {
        targetCall.abi = Sumer.abi.CErc20
        targetCall.args = [quotedExpenseAmt]
      }

      const wrappedCall = withExecAndTransfer({
        call: targetCall,
        transfer: {
          token: TargetAddress,
          to: account,
        }
      });
      const orderParams = {
        srcChainId: currentNetwork.networkId,
        destChainId: destChain.networkId,
        deposit: { token: fromToken.underlyAddr, amount: quotedDepositAmt },
        expense: { token: destToken.underlyAddr, amount: quotedExpenseAmt, spender: TargetAddress },
        calls: [wrappedCall],
        environment
      }

      // generate order
      const generator = generateOrder({
        client: walletClient,
        inboxAddress: inbox,
        order: orderParams,
        environment
      })

      commit('setTxInfos', { id, status: 'pending', msg: 'Transaction pending.' })

      let orderState
      for await (orderState of generator) {
        console.log('current order state', orderState)
        commit('setTxInfos', { id, status: 'pending', msg: `Transaction ${orderState.status}` })

        if (orderState.status === 'open') {
          break
        }
       }


      commit('setTxInfos', { id, hash: orderState.txHash, status: 'ok', msg: 'Transaction success.' })

      dispatch('market/initMarket', null, { root: true })
    } catch (e) {
      console.log('Deposit by omni', e)
      const msg = getErrorMsg(e)
      commit('setTxInfos', { id, status: 'fail', msg })
    }
  },
  async badConnect({ rootState }) {
    const { chainId, web3Provider } = rootState.wallet

    console.log('old chain', chainId)
    const currNetwork = await web3Provider.getNetwork()
    console.log('currNetwork', currNetwork)
    if (currNetwork.chainId != chainId) {
      return 'The wallet is connected to a blockchain inconsisent with the current Sumer page, please refresh the page to proceed.'
    }
    return false
  }
}

export const tx = {
  namespaced,
  state,
  getters,
  mutations,
  actions,
}

const camelToSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => ` ${letter.toLowerCase()}`)

const getErrorMsg = (e) => {
  try {
    console.log('err', { ...e })
    let msg = ''
    if (e.error) {
      msg = e.error.reason || e.error.shortMessage
      if (e.error.data) {
        try {
          const intf = new Sumer._ethers.Interface(Sumer.errorABIs)
          const decodedError = intf.parseError(e.error.data)
          console.log('decodedError', decodedError)
          msg = camelToSnakeCase(decodedError.name)
        } catch (e) {
          msg = UnknowError
        }
      }
    } else if (e.data) {
      const intf = new Sumer._ethers.Interface(Sumer.errorABIs)
      const decodedError = intf.parseError(e.data)
      console.log('decodedError', decodedError)
      msg = camelToSnakeCase(decodedError.name)
    } else if (e.shortMessage) {
      msg = e.shortMessage
    } else if (e.reason) {
      msg = e.reason
    } else {
      msg = UnknowError
    }

    if (msg === 'insufficient funds') {
      msg = 'insufficient gas'
    }

    return msg
  } catch(e) {
    console.log('getErrorMsg err', e)
    return UnknowError
  }
}
